import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { displayDate } from '@/lib/dates';

export function auditTime(value, timezone) {
  if (!value) return '—';
  if (/^\d{2}:\d{2}$/.test(value)) return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return displayDate(value);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('en-NZ', { timeZone: timezone, day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
const valueText = value => Array.isArray(value) ? value.join(', ') : typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value ?? '—');

export default function SiteAuditView({ data, openDocument }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function open(path) {
    setBusy(true); setError('');
    try { await openDocument(path); } catch (e) { setError(e.message || 'Document unavailable'); }
    finally { setBusy(false); }
  }
  return <div className="space-y-5 text-[#102130]">
    <header className="border-t-4 border-[#FF5F15] pt-5">
      <p className="text-xs font-bold tracking-widest text-[#c7440a]">SITE AUDIT</p>
      <p className="mt-3 text-sm">{data.job.job_number}</p>
      <h1 className="text-2xl font-bold">{data.job.title}</h1>
      <p className="mt-2 text-sm">{data.job.site_address}</p>
      <p className="mt-3 text-sm">{displayDate(data.date)} · Read-only site safety information</p>
      {data.pass && <p className="mt-2 text-sm">Pass expires {auditTime(data.pass.expires_at, data.timezone)}</p>}
    </header>
    <div className="rounded-xl border bg-white p-4">
      <h2 className="font-semibold">Record availability</h2>
      <p className="mt-1">{data.available} of {data.configured} configured record groups available</p>
      <p className="mt-2 text-xs text-slate-600">{data.limit_note}</p>
    </div>
    {error && <p role="alert" className="text-red-700">{error}</p>}
    {data.groups.filter(group => group.state !== 'not_configured').map(group => <details key={group.key} className="rounded-xl border bg-white">
      <summary className="cursor-pointer p-4 font-semibold min-h-12">{group.label} <span className="ml-2 text-sm font-normal text-slate-600">{group.records.length ? `${group.records.length} records` : 'No records available'}</span></summary>
      <div className="space-y-3 px-4 pb-4">
        {group.records.length === 0 && <p className="text-sm text-slate-600">No permitted records available in the selected period.</p>}
        {group.records.map((record, i) => <article key={i} className="border-t pt-3 break-words">
          <h3 className="font-medium whitespace-pre-wrap">{record.title}</h3>
          {record.subtitle && <p className="text-sm">{record.subtitle}</p>}
          {record.status && <p className="text-sm text-slate-700 mt-1">{record.status}</p>}
          {record.date && <p className="text-xs text-slate-600 mt-1">{auditTime(record.date, data.timezone)}</p>}
          {record.expiry && <p className="text-sm">Recorded expiry: {displayDate(record.expiry)}</p>}
          {record.detail?.filter(item => item.value != null && item.value !== '').map((item, j) => <div className="mt-3 text-sm" key={j}><p className="font-medium">{item.label}</p><p className="whitespace-pre-wrap">{valueText(item.value)}</p></div>)}
          {record.hazards?.map((hazard, j) => <div className="mt-3 text-sm" key={j}><p className="font-medium">{hazard.task}</p><p>{valueText(hazard.hazards)}</p><p>{valueText(hazard.controls)}</p><p>Recorded residual risk: {hazard.risk_after_controls || '—'}</p></div>)}
          {record.document && <Button variant="outline" className="mt-3 min-h-11" disabled={busy} onClick={() => open(record.document)}>View permitted document</Button>}
        </article>)}
      </div>
    </details>)}
    {data.pass && <div className="text-xs text-slate-600">Generated {auditTime(data.pass.created_at, data.timezone)}<br/>Expires {auditTime(data.pass.expires_at, data.timezone)}</div>}
    <footer className="text-xs leading-relaxed text-slate-600 pb-5">This page provides read-only access to selected records made available by the site operator through Fieldlink.<br/>Information shown reflects records available in Fieldlink at the time of viewing.</footer>
  </div>;
}
